import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, useOutletContext } from 'react-router-dom';
import { supabase } from '../../auth/SupabaseClient';
import { User, Mail, Phone, Instagram, MapPin, Calendar, FileText, ArrowLeft, Save, Edit, X } from 'lucide-react';

interface Client {
  id: number;
  name: string;
  email: string;
  telephone: string;
  instagram: string;
  tags: string[];
  adresse: string;
  ville: string;
  code_postal: string;
  pays: string;
  date_naissance: string;
  notes: string;
}

const ClientDetails: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [client, setClient] = useState<Client | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editingField, setEditingField] = useState<string | null>(null);
  const [editedValues, setEditedValues] = useState<Partial<Client>>({});
  const inputRef = useRef<HTMLInputElement | HTMLTextAreaElement>(null);
  const navigate = useNavigate();
  const { darkMode } = useOutletContext<{ darkMode: boolean }>();

  useEffect(() => {
    const fetchClient = async () => {
      try {
        const { data, error } = await supabase
          .from('clients')
          .select('*')
          .eq('id', id)
          .single();

        if (error) throw error;
        setClient(data);
        setEditedValues(data);
      } catch (error) {
        console.error('Error fetching client:', error);
        setError('An error occurred while fetching the client details.');
      } finally {
        setLoading(false);
      }
    };

    fetchClient();
  }, [id]);

  useEffect(() => {
    if (editingField && inputRef.current) {
      inputRef.current.focus();
    }
  }, [editingField]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setEditedValues(prev => ({ ...prev, [name]: value }));
  };

  const handleSave = async (fieldName: string) => {
    if (!client) return;

    try {
      const { data, error } = await supabase
        .from('clients')
        .update({ [fieldName]: editedValues[fieldName as keyof Client] })
        .eq('id', id)
        .select();
    
      if (error) throw error;
    
      if (data && data.length > 0) {
        setClient(data[0]);
        setEditedValues(data[0]);
      }
      setEditingField(null);
    } catch (error) {
      console.error('Error updating client:', error);
      setError('An error occurred while updating the client details.');
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent, fieldName: string) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSave(fieldName);
    } else if (e.key === 'Escape') {
      setEditingField(null);
      setEditedValues(prev => ({ ...prev, [fieldName]: client?.[fieldName as keyof Client] || '' }));
    }
  };

  if (loading) return <div className="flex justify-center items-center h-screen">
    <div className={`animate-spin rounded-full h-32 w-32 border-t-2 border-b-2 ${darkMode ? 'border-blue-400' : 'border-blue-500'}`}></div>
  </div>;
  if (error) return <div className={`text-red-500 text-center text-xl mt-10 ${darkMode ? 'bg-gray-800' : 'bg-white'}`}>{error}</div>;
  if (!client) return <div className={`text-center text-xl mt-10 ${darkMode ? 'text-gray-300 bg-gray-800' : 'text-gray-500 bg-white'}`}>Client not found</div>;

  return (
    <div className={`w-full shadow-lg rounded-lg ${darkMode ? 'bg-gray-800 text-white' : 'bg-white text-gray-800'}`}>
      <div className={`px-6 py-4 ${darkMode ? 'bg-gradient-to-r from-blue-700 to-indigo-800' : 'bg-gradient-to-r from-blue-500 to-indigo-600'} text-white flex justify-between items-center rounded-t-lg`}>
        <div>
          <h3 className="text-2xl font-bold">Client Information</h3>
          <p className="text-sm opacity-80">Personal details and contact information</p>
        </div>
        <button
          onClick={() => navigate('/dashboard/clients')}
          className={`flex items-center px-4 py-2 text-sm font-medium rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition duration-150 ease-in-out ${
            darkMode
              ? 'text-white bg-gray-700 hover:bg-gray-600'
              : 'text-slate-950 bg-white hover:bg-blue-50'
          }`}
        >
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back to Clients
        </button>
      </div>
      <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-6">
        {renderField("Full name", client.name, "name", <User className={darkMode ? "text-white" : "text-slate-950"} size={20} />)}
        {renderField("Email address", client.email, "email", <Mail className={darkMode ? "text-white" : "text-slate-950"} size={20} />)}
        {renderField("Phone number", client.telephone, "telephone", <Phone className={darkMode ? "text-white" : "text-slate-950"} size={20} />)}
        {renderField("Instagram", client.instagram, "instagram", <Instagram className={darkMode ? "text-white" : "text-slate-950"} size={20} />)}
        {renderField("Address", client.adresse, "adresse", <MapPin className={darkMode ? "text-white" : "text-slate-950"} size={20} />)}
        {renderField("City", client.ville, "ville")}
        {renderField("Postal Code", client.code_postal, "code_postal")}
        {renderField("Country", client.pays, "pays")}
        {renderField("Date of birth", client.date_naissance, "date_naissance", <Calendar className={darkMode ? "text-white" : "text-slate-950"} size={20} />)}
        {renderField("Notes", client.notes, "notes", <FileText className={darkMode ? "text-white" : "text-slate-950"} size={20} />, true)}
      </div>
    </div>
  );

  function renderField(label: string, value: string, name: string, icon?: React.ReactNode, isTextarea: boolean = false) {
    const isEditing = editingField === name;

    return (
      <div className={`rounded-lg p-4 transition duration-150 ease-in-out hover:shadow-md ${darkMode ? 'bg-gray-700' : 'bg-gray-50'}`}>
        <div className="flex items-center justify-between mb-2">
          <div className={`flex items-center font-medium text-lg ${darkMode ? 'text-white' : 'text-slate-950'}`}>
            {icon && <span className="mr-2">{icon}</span>}
            {label}
          </div>
          {!isEditing && (
            <button
              onClick={() => setEditingField(name)}
              className={`hover:text-blue-500 focus:outline-none transition duration-150 ease-in-out ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}
            >
              <Edit size={18} />
            </button>
          )}
        </div>
        {isEditing ? (
          <div className="mt-2">
            {isTextarea ? (
              <textarea
                ref={inputRef as React.RefObject<HTMLTextAreaElement>}
                name={name}
                value={editedValues[name as keyof Client] || ''}
                onChange={handleInputChange}
                onKeyDown={(e) => handleKeyDown(e, name)}
                className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:border-blue-500 ${
                  darkMode
                    ? 'bg-gray-600 text-white border-gray-500'
                    : 'bg-white text-gray-700 border-gray-300'
                }`}
                rows={3}
              />
            ) : (
              <input
                ref={inputRef as React.RefObject<HTMLInputElement>}
                type={name === "date_naissance" ? "date" : "text"}
                name={name}
                value={editedValues[name as keyof Client] || ''}
                onChange={handleInputChange}
                onKeyDown={(e) => handleKeyDown(e, name)}
                className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:border-blue-500 ${
                  darkMode
                    ? 'bg-gray-600 text-white border-gray-500'
                    : 'bg-white text-gray-700 border-gray-300'
                }`}
              />
            )}
            <div className="flex justify-end mt-2 space-x-2">
              <button
                onClick={() => handleSave(name)}
                className="px-3 py-1 bg-green-500 text-white rounded-md hover:bg-green-600 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-opacity-50 transition duration-150 ease-in-out"
              >
                <Save size={18} />
              </button>
              <button
                onClick={() => {
                  setEditingField(null);
                  setEditedValues(prev => ({ ...prev, [name]: client?.[name as keyof Client] || '' }));
                }}
                className="px-3 py-1 bg-red-500 text-white rounded-md hover:bg-red-600 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-opacity-50 transition duration-150 ease-in-out"
              >
                <X size={18} />
              </button>
            </div>
          </div>
        ) : (
          <div className={`mt-1 p-3 rounded-md border ${
            darkMode
              ? 'bg-gray-600 text-gray-200 border-gray-500'
              : 'bg-white text-gray-700 border-gray-200'
          }`}>
            {value || 'Not provided'}
          </div>
        )}
      </div>
    );
  }
};

export default ClientDetails;